// The resting still of a Library video tile, cached in the browser so a
// revisit paints instantly instead of re-paying a Store read. The tile
// itself captures the still (views/Library.tsx LibraryTile); this module
// only stores and returns it, so it can be unit-tested without a canvas.
//
// The video bytes can never come from the browser cache: a <video>
// element's Range requests are answered `206` and Chromium does not reuse
// a partial response for a media element (measured -- it re-requests both
// ranges from a brand-new tab), while a plain `200` image response with
// the same `Cache-Control` is reused. So the still is captured once from a
// decoded frame as a small JPEG data URL and kept here, keyed by artifact
// id. An artifact's bytes never change after commit, so a captured still
// never goes stale.
//
// The cache lives in one browser profile: the first visit in a given
// browser still pays the tile's full cost; every visit afterwards paints
// from the still with no media request.
//
// Every path is best-effort. Storage may be disabled (throws on access) or
// full (setItem throws QuotaExceededError); neither may break a tile, so
// nothing in this module throws.

// v2: the v1 stills were captured from a paused <video> at t=0, which has
// no presented frame for drawImage -- every one of them is black. The
// version makes them unreachable so a tile captures a real frame instead
// of resting on a black thumbnail forever. (The sweep below reclaims their
// space.)
const KEY_PREFIX = "gpufan.still.v2:";
// Insertion order of the stills, oldest first, so a quota failure knows
// what to drop.
const ORDER_KEY = "gpufan.still.order.v2";

// The v1 keys, read only by the one-shot sweep.
const LEGACY_KEY_PREFIX = "gpufan.still:";
const LEGACY_ORDER_KEY = "gpufan.still.order";

// The sweep runs at most once per page load, on the first cache read or
// write.
let legacySwept = false;

/** localStorage, or undefined where reading it throws (e.g. storage
 * disabled by the browser). */
function storage(): Storage | undefined {
  try {
    return window.localStorage;
  } catch {
    return undefined;
  }
}

function stillKey(id: string): string {
  return KEY_PREFIX + id;
}

/** Drop the v1 stills and their order key, once. Best-effort and silent:
 * they are black and would never be replaced otherwise, so clearing them
 * just returns the quota they were holding. */
function sweepLegacy(): void {
  if (legacySwept) return;
  legacySwept = true;
  const store = storage();
  if (!store) return;
  try {
    const doomed = [LEGACY_ORDER_KEY];
    for (let i = 0; i < store.length; i++) {
      const key = store.key(i);
      // The v2 keys carry ".v2:" here, so they cannot match.
      if (key !== null && key.startsWith(LEGACY_KEY_PREFIX)) doomed.push(key);
    }
    for (const key of doomed) {
      try {
        store.removeItem(key);
      } catch {
        /* nothing more the sweep can do */
      }
    }
  } catch {
    /* storage unavailable: the v2 keys are unaffected either way */
  }
}

/** The cached still for an artifact, or undefined when none was captured
 * (or storage is unavailable). Synchronous, so a tile's first render can
 * hand it straight to the video's `poster`. */
export function readStill(id: string): string | undefined {
  sweepLegacy();
  try {
    return storage()?.getItem(stillKey(id)) ?? undefined;
  } catch {
    return undefined;
  }
}

/** Cache a freshly captured still. Never throws: on a quota error the
 * oldest entry is evicted and the write is retried once, and if that
 * fails too the still is simply not cached. */
export function storeStill(id: string, dataUrl: string): void {
  sweepLegacy();
  const store = storage();
  if (!store) return;
  try {
    store.setItem(stillKey(id), dataUrl);
  } catch {
    evictOldest(store);
    try {
      store.setItem(stillKey(id), dataUrl);
    } catch {
      return; // stills are a nicety; never let storage break the tile
    }
  }
  recordOrder(store, id);
}

function readOrder(store: Storage): string[] {
  try {
    const raw = store.getItem(ORDER_KEY);
    const parsed: unknown = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed.filter((x): x is string => typeof x === "string") : [];
  } catch {
    return [];
  }
}

function writeOrder(store: Storage, order: string[]): void {
  try {
    store.setItem(ORDER_KEY, JSON.stringify(order));
  } catch {
    /* the order is best-effort; losing it only weakens eviction */
  }
}

/** Mark an id as the newest still (moving it to the end if present). */
function recordOrder(store: Storage, id: string): void {
  const order = readOrder(store).filter((x) => x !== id);
  order.push(id);
  writeOrder(store, order);
}

/** Drop the oldest still, freeing space for the retry. */
function evictOldest(store: Storage): void {
  const order = readOrder(store);
  const oldest = order[0];
  if (oldest !== undefined) {
    try {
      store.removeItem(stillKey(oldest));
    } catch {
      /* nothing more eviction can do */
    }
  }
  writeOrder(store, order.slice(1));
}
